import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardBody, Col, Form } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import api from "@/api/api.js";
import { TAX_REGIMES } from "@/config/satCatalogs.js";
import CompanyFields from "@/pages/shared/sections/CompanyFields.jsx";
import FiscalDocumentUpload from "@/pages/shared/sections/FiscalDocumentUpload.jsx";
import SignatureUpload from "@/pages/shared/sections/SignatureUpload.jsx";
import { usePageTitle } from "@/context/PageTitleContext.jsx";

const initialForm = {
  type: "fisica",
  businessName: "",
  name: "",
  taxIdentification: "",
  taxRegime: "",
  street: "",
  exteriorNumber: "",
  interiorNumber: "",
  suburb: "",
  municipality: "",
  state: "",
  postalCode: "",
  phone: "",
  email: "",
};

const PREFILLABLE = [
  "type",
  "name",
  "taxIdentification",
  "taxRegime",
  "street",
  "exteriorNumber",
  "interiorNumber",
  "suburb",
  "municipality",
  "state",
  "postalCode",
  "email",
];

const ConstanciaDropzone = ({ onParsed }) => {
  const { t } = useTranslation();
  const [parsing, setParsing] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      const formData = new FormData();
      formData.append("file", acceptedFiles[0]);
      setParsing(true);
      try {
        const { data } = await api.post(
          "/companies/constancia/parse",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        onParsed(data);
        Swal.fire({
          icon: "success",
          title: "Constancia leída",
          text: t("ui.accountParsedHint"),
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "No se pudo leer la constancia",
          text:
            err?.response?.data?.error ||
            "Verifica que el PDF sea una constancia de situación fiscal vigente.",
        });
      } finally {
        setParsing(false);
      }
    },
    [onParsed, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
    multiple: false,
    disabled: parsing,
  });

  return (
    <div
      {...getRootProps()}
      className="dropzone"
      style={{
        border: "2px dashed #ccc",
        padding: "20px",
        textAlign: "center",
        cursor: parsing ? "wait" : "pointer",
        borderRadius: "4px",
      }}
    >
      <input {...getInputProps()} />
      <i className="bi bi-file-earmark-pdf fs-3 d-block mb-1 text-secondary" />
      {parsing ? (
        <p className="mb-0">Leyendo constancia...</p>
      ) : isDragActive ? (
        <p className="mb-0">Suelta el PDF aquí...</p>
      ) : (
        <p className="mb-0">
          Arrastra tu constancia de situación fiscal (PDF) o haz click para
          autollenar el formulario.
        </p>
      )}
    </div>
  );
};

const FirstCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [companyId, setCompanyId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [constanciaDone, setConstanciaDone] = useState(false);
  const [fielDone, setFielDone] = useState(false);
  const [hasCompanies, setHasCompanies] = useState(false);
  const [existingCompanyId, setExistingCompanyId] = useState(null);
  const [constanciaInfo, setConstanciaInfo] = useState(null);

  useEffect(() => {
    api
      .get("/companies", { params: { itemsPerPage: 1 } })
      .then(({ data }) => {
        const existing = data["hydra:member"]?.[0] ?? null;
        setHasCompanies(Boolean(existing));
        setExistingCompanyId(existing?.id ?? null);
      })
      .catch(() => setHasCompanies(false));
  }, []);

  // Una cuenta = un usuario = una empresa: quien ya la tiene no pasa por el alta,
  // va a su ficha. El servidor también lo rechaza, esto sólo evita el viaje.
  useEffect(() => {
    if (existingCompanyId && !companyId) {
      navigate(`/companies/${existingCompanyId}`, { replace: true });
    }
  }, [existingCompanyId, companyId, navigate]);

  const isFirst = !hasCompanies;

  // Los dos pasos del alta comparten ruta: cada uno pone su propio título y miga.
  usePageTitle(
    companyId
      ? t("page.fiscalDocuments")
      : isFirst
        ? t("ui.generateAccount")
        : t("ui.registerAccount"),
    companyId ? t("page.fiscalDocumentsCrumb") : undefined,
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleParsed = (data) => {
    setForm((prev) => {
      const next = { ...prev };
      PREFILLABLE.forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
          next[key] = data[key];
        }
      });
      return next;
    });
    setConstanciaInfo({
      lugarEmision: data.lugarEmision || "",
      fechaEmision: data.fechaEmision || "",
      regimenes: Array.isArray(data.regimenes) ? data.regimenes : [],
    });
  };

  const regimes = TAX_REGIMES.filter(
    (r) => r.type === form.type || r.type === "ambos",
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const response = await api.post("/companies", form);
      setCompanyId(response.data.id);
    } catch (err) {
      const violations = err.response?.data?.violations;
      if (violations?.length) {
        setError(violations.map((v) => v.message).join(" "));
      } else {
        setError(
          err.response?.data?.["hydra:description"] ||
            t("ui.accountCreateError"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (companyId) {
    return (
      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3">
              <h5 className="mb-0">{t("page.fiscalDocumentsCrumb")}</h5>
              <small className="text-muted">
                Sube tu constancia de situación fiscal y tu FIEL (.cer + .key
                con contraseña) para poder facturar.
              </small>
            </div>

            <h6 className="text-primary fw-bold mb-2">
              Constancia de situación fiscal
            </h6>
            <FiscalDocumentUpload
              companyId={companyId}
              onUploadSuccess={() => setConstanciaDone(true)}
            />
            {constanciaDone && (
              <Alert variant="success" className="py-2">
                Constancia cargada correctamente.
              </Alert>
            )}

            <h6 className="text-primary fw-bold mb-2 mt-3">
              FIEL / Certificado (.cer, .key y contraseña)
              <span className="text-danger ms-1">*</span>
            </h6>
            <SignatureUpload
              companyId={companyId}
              onUploadSuccess={() => setFielDone(true)}
            />

            <div className="border-top pt-3 mt-3 d-flex align-items-center gap-3">
              {fielDone ? (
                <span className="me-auto" />
              ) : (
                <small className="text-muted ms-auto">
                  Debes subir tu certificado (.cer), llave (.key) y contraseña
                  para continuar.
                </small>
              )}
              <Button
                variant="primary"
                onClick={() => navigate("/buy_package")}
                disabled={!fielDone}
              >
                Continuar a comprar paquete
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Col xs={12}>
        <Card>
          <CardBody>
            <div className="border-bottom pb-2 mb-3">
              <h5 className="mb-0">{t("ui.enterYourData")}</h5>
              <small className="text-muted">
                Sube tu constancia para autollenar los datos, o captúralos
                manualmente.
              </small>
            </div>
            <ConstanciaDropzone onParsed={handleParsed} />

            {constanciaInfo && (
              <div className="border-top mt-3 pt-3">
                <h6 className="text-primary fw-bold mb-2">
                  Información de la constancia
                </h6>
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <small className="text-muted d-block">
                      Lugar de emisión
                    </small>
                    <span>{constanciaInfo.lugarEmision || "—"}</span>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block">
                      Fecha de emisión
                    </small>
                    <span>{constanciaInfo.fechaEmision || "—"}</span>
                  </div>
                </div>
                <small className="text-muted d-block mb-1">
                  Regímenes registrados
                </small>
                {constanciaInfo.regimenes.length === 0 ? (
                  <span className="text-muted">—</span>
                ) : (
                  <ul className="list-group">
                    {constanciaInfo.regimenes.map((r, i) => (
                      <li
                        key={`${r.id}-${i}`}
                        className="list-group-item d-flex justify-content-between align-items-start"
                      >
                        <span>
                          {r.id ? `${r.id} — ` : ""}
                          {r.descripcion || "—"}
                        </span>
                        {r.fechaAlta && (
                          <small className="text-muted ms-2">
                            Alta: {r.fechaAlta}
                          </small>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      <div className="col">
        <div className="card">
          <div className="card-body">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <CompanyFields
                form={form}
                handleChange={handleChange}
                regimes={regimes}
              />

              <div className="d-flex align-items-center gap-2 mt-3 mb-2">
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? "Creando..." : t("ui.createAccount")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(isFirst ? -1 : "/my-companies")}
                >
                  Cancelar
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FirstCompany;
